import React, { useState } from "react";
import { Pencil, Check, X } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";

/**
 * SPT-PTE-2026-0033 — lightweight inline pen-edit for a job item's invoice
 * number, used on the Dispatch "Closed" view so admin staff can record/
 * correct an invoice reference on an already-paid/closed line WITHOUT
 * opening the job and WITHOUT the status regressing away from Closed (the
 * pre-existing "Capture Invoice" button always forces payment_status ->
 * "awaiting_payment", which is the correct behavior for a NOT-yet-invoiced
 * line but wrong for this quick correction). Omits `payment_status`
 * entirely from the PATCH payload so the line's current payment state is
 * left exactly as-is.
 */
export function InlineInvoiceEdit({ item, onSaved, testidPrefix = "inline-invoice" }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(item.invoice_number || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const trimmed = value.trim();
    if (!trimmed) { toast.message("Enter an invoice number first"); return; }
    setBusy(true);
    try {
      await api.patch(`/job-items/${item.id}/payment`, { invoice_number: trimmed });
      toast.success("Invoice number updated");
      setEditing(false);
      onSaved && onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (!editing) {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
        <span className="mono" style={{ fontSize: 11.5 }} data-testid={`${testidPrefix}-value-${item.id}`}>
          {item.invoice_number || <span className="t-mut">—</span>}
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); setValue(item.invoice_number || ""); setEditing(true); }}
          title="Edit invoice number"
          data-testid={`${testidPrefix}-edit-${item.id}`}
          style={{ background: "none", border: "none", cursor: "pointer", padding: 2, color: "var(--text-3)", display: "inline-flex" }}>
          <Pencil size={11} />
        </button>
      </span>
    );
  }

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }} onClick={(e) => e.stopPropagation()}>
      <input
        className="input mono"
        style={{ width: 110, fontSize: 11.5, padding: "2px 6px" }}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Invoice #"
        autoFocus
        disabled={busy}
        data-testid={`${testidPrefix}-input-${item.id}`}
        onKeyDown={(e) => {
          if (e.key === "Enter") save();
          if (e.key === "Escape") setEditing(false);
        }}
      />
      <button onClick={save} disabled={busy} title="Save"
        data-testid={`${testidPrefix}-save-${item.id}`}
        style={{ background: "none", border: "none", cursor: "pointer", color: "var(--gold-deep)", display: "inline-flex" }}>
        <Check size={13} />
      </button>
      <button onClick={() => setEditing(false)} disabled={busy} title="Cancel"
        data-testid={`${testidPrefix}-cancel-${item.id}`}
        style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-3)", display: "inline-flex" }}>
        <X size={13} />
      </button>
    </span>
  );
}
